import { useCallback, useRef } from "react";

import { tv, type VariantProps } from "tailwind-variants";

import { BREAKPOINTS } from "./Panel";

export const panelResizeHandleVariants = tv({
	base: [
		"relative z-10 shrink-0 select-none",
		"w-0 before:absolute before:inset-y-0 before:-left-[3px] before:w-[7px] before:content-['']",
		"after:bg-border after:absolute after:inset-y-0 after:left-0 after:-ml-px after:w-px after:content-['']",
	],
	variants: {
		isDisabled: {
			true: "cursor-default",
			false: "cursor-col-resize hover:after:w-0.5 hover:after:bg-primary-active active:after:w-0.5 active:after:bg-primary-active",
		},
	},
	defaultVariants: {
		isDisabled: false,
	},
});

export type PanelResizeHandleVariant = VariantProps<typeof panelResizeHandleVariants>;

const DEFAULT_MIN_RATIO = 0.1;
const DEFAULT_MAX_RATIO = 0.9;

function getBreakpointWidth(name: string): number {
	if (name === "base") return 0;

	const value = getComputedStyle(document.documentElement).getPropertyValue(
		`--breakpoint-${name}`,
	);

	return parseFloat(value) * (value.endsWith("rem") ? 16 : 1) || 0;
}

type ResizeConstraints = { min?: number; max?: number };

function isResizeConstraintsRecord(value: unknown): value is Record<string, ResizeConstraints> {
	return typeof value === "object" && value !== null;
}

function getResizeConstraints(el: HTMLElement): ResizeConstraints {
	const raw = el.dataset.resizeConstraints;

	if (!raw) return {};

	try {
		const parsed: unknown = JSON.parse(raw);

		if (!isResizeConstraintsRecord(parsed)) return {};

		const windowWidth = window.innerWidth;
		let result: ResizeConstraints = {};

		for (const bp of BREAKPOINTS) {
			if (parsed[bp] && windowWidth >= getBreakpointWidth(bp)) {
				result = { ...result, ...parsed[bp] };
			}
		}

		return result;
	} catch {
		return {};
	}
}

function getAdjacentPanels(handle: HTMLElement): {
	prevPanel: HTMLElement;
	nextPanel: HTMLElement;
} {
	const prevPanel = handle.previousElementSibling;
	const nextPanel = handle.nextElementSibling;

	if (!(prevPanel instanceof HTMLElement) || !(nextPanel instanceof HTMLElement)) {
		throw new Error("PanelResizeHandle must be placed between two Panel elements");
	}

	return { prevPanel, nextPanel };
}

function computeRatioBounds(
	prevPanel: HTMLElement,
	nextPanel: HTMLElement,
	parentWidth: number,
	combinedWidth: number,
): { minRatio: number; maxRatio: number } {
	const prevConstraints = getResizeConstraints(prevPanel);
	const nextConstraints = getResizeConstraints(nextPanel);

	let minRatio = DEFAULT_MIN_RATIO;
	let maxRatio = DEFAULT_MAX_RATIO;

	if (prevConstraints.min !== undefined) {
		minRatio = Math.max(minRatio, ((prevConstraints.min / 100) * parentWidth) / combinedWidth);
	}

	if (prevConstraints.max !== undefined) {
		maxRatio = Math.min(maxRatio, ((prevConstraints.max / 100) * parentWidth) / combinedWidth);
	}

	if (nextConstraints.min !== undefined) {
		maxRatio = Math.min(
			maxRatio,
			1 - ((nextConstraints.min / 100) * parentWidth) / combinedWidth,
		);
	}

	if (nextConstraints.max !== undefined) {
		minRatio = Math.max(
			minRatio,
			1 - ((nextConstraints.max / 100) * parentWidth) / combinedWidth,
		);
	}

	return { minRatio, maxRatio };
}

type PanelResizeHandleProps = PanelResizeHandleVariant & {
	readonly className?: string;
};

export function PanelResizeHandle({ isDisabled, className }: PanelResizeHandleProps) {
	const ref = useRef<HTMLDivElement>(null);

	const onPointerDown = useCallback(
		(e: React.PointerEvent) => {
			if (isDisabled) return;

			e.preventDefault();

			const handle = ref.current!;

			handle.setPointerCapture(e.pointerId);

			const { prevPanel, nextPanel } = getAdjacentPanels(handle);
			const parentWidth = handle.parentElement!.getBoundingClientRect().width;

			prevPanel.style.transition = "none";
			nextPanel.style.transition = "none";

			const prevRect = prevPanel.getBoundingClientRect();
			const nextRect = nextPanel.getBoundingClientRect();
			const combinedWidth = prevRect.width + nextRect.width;
			const combinedBasis = (combinedWidth / parentWidth) * 100;

			const onPointerMove = (ev: PointerEvent) => {
				const relativeX = ev.clientX - prevPanel.getBoundingClientRect().left;

				const { minRatio, maxRatio } = computeRatioBounds(
					prevPanel,
					nextPanel,
					parentWidth,
					combinedWidth,
				);

				const ratio = Math.max(minRatio, Math.min(maxRatio, relativeX / combinedWidth));

				prevPanel.style.flexBasis = `${combinedBasis * ratio}%`;
				prevPanel.style.flexGrow = "0";
				nextPanel.style.flexBasis = `${combinedBasis * (1 - ratio)}%`;
				nextPanel.style.flexGrow = "0";
			};

			const onPointerUp = () => {
				prevPanel.style.transition = "";
				nextPanel.style.transition = "";
				handle.removeEventListener("pointermove", onPointerMove);
				handle.removeEventListener("pointerup", onPointerUp);
			};

			handle.addEventListener("pointermove", onPointerMove);
			handle.addEventListener("pointerup", onPointerUp);
		},
		[isDisabled],
	);

	return (
		<div
			ref={ref}
			role="separator"
			aria-orientation="horizontal"
			aria-disabled={isDisabled}
			className={panelResizeHandleVariants({ isDisabled: Boolean(isDisabled), className })}
			onPointerDown={onPointerDown}
		/>
	);
}
